import { RouteObject, useRoutes, useNavigate } from 'react-router-dom';
import { BaseLayout } from './page-base-layout';
import { ConnectedWalletLayout } from './wallet/connectedWallet';
import { DigitalSignatureEmbeddingDialog } from './files/dialogs/digital-sign-embedding-modal';
import { AssigneeInterfaceWrapper } from './files/dialogs/AssigneeInterfaceWrapper';
import { useAccount } from 'wagmi';
import { Dashboard } from './dashboard';
import { NoWallet } from './wallet/no-wallet';
import { DocumentsLayout } from './documents';
import { MintedLayout } from './minted';
import { TransactionsTable } from './files/table/transactions-table';
import { PaymentHistoryTable } from './files/table/payment-history-table';
import { RouteLoaderWrapper } from './RouteLoaderWrapper';
import { useEffect, useState } from 'react';
import { useDriveStore } from './drive-store';
import { NotFoundPage } from '@app/ui/not-found-page/not-found-page';

// Check signature provider from env
const useCustomSignature = () => {
  const provider = import.meta.env.VITE_SIGNATURE_PROVIDER || 'docuseal';
  return provider === 'custom';
};

export default function DriveRoutes() {
  const { status } = useAccount();
  const setWalletStatus = useDriveStore(s => s.setWalletStatus);
  const walletStatus = useDriveStore(s => s.walletStatus);
  const resetDriveStore = useDriveStore(s => s.reset);
  const navigate = useNavigate();

  // Check Gmail authentication (check on every render to catch login changes)
  const isCustom = useCustomSignature();
  
  // Check Gmail auth directly from localStorage (reactive to changes)
  const checkGmailAuth = () => {
    const gmailToken = localStorage.getItem('authToken');
    const authType = localStorage.getItem('authType');
    return !!gmailToken && authType === 'gmail';
  };
  
  const [isGmailAuth, setIsGmailAuth] = useState(checkGmailAuth);

  useEffect(() => {
    console.log(`🖊️ Signature Provider: ${isCustom ? 'Custom' : 'DocuSeal'}`);
  }, [isCustom]);

  // Update Gmail auth state on mount and when location changes
  useEffect(() => {
    const updateGmailAuth = () => {
      const newAuthState = checkGmailAuth();
      console.log('Gmail Auth State:', newAuthState);
      setIsGmailAuth(newAuthState);
    };
    
    updateGmailAuth();
    
    // Listen for storage events (when another tab logs in)
    window.addEventListener('storage', updateGmailAuth);
    
    return () => {
      window.removeEventListener('storage', updateGmailAuth);
    };
  }, []);

  useEffect(() => {
    setWalletStatus(status);
  }, [status, setWalletStatus]);

  useEffect(() => {
    // Only disconnect if NO auth method is active
    if (status === 'disconnected' && walletStatus !== 'disconnected' && !isGmailAuth) {
      resetDriveStore();
      setWalletStatus('disconnected');
      navigate('/');
    }
  }, [status, walletStatus, isGmailAuth, resetDriveStore, setWalletStatus, navigate]);

  const isLoading =
    walletStatus === 'reconnecting' ||
    walletStatus === 'connecting' ||
    walletStatus === 'unknown';

  // Connected if EITHER wallet OR gmail
  const isActuallyConnected = walletStatus === 'connected' || isGmailAuth;
  
  // Debug logging
  useEffect(() => {
    console.log('🔐 Auth Status:', {
      walletStatus,
      isGmailAuth,
      isActuallyConnected,
      authToken: localStorage.getItem('authToken') ? 'Present' : 'Missing',
      authType: localStorage.getItem('authType'),
      userEmail: localStorage.getItem('userEmail')
    });
  }, [walletStatus, isGmailAuth, isActuallyConnected]);

  const routes: RouteObject[] = [
    {
      path: '/',
      element: (
        <BaseLayout isConnected={isActuallyConnected}>
          <RouteLoaderWrapper isLoading={isLoading}>
            {(() => {
              // Check for signing mode via query params at root
              const searchParams = new URLSearchParams(window.location.search);
              const mode = searchParams.get('mode');
              const docId = searchParams.get('docId');
              const token = searchParams.get('token');

              // If signing mode with params AND using custom provider
              if (mode === 'sign' && docId && token && isCustom) {
                return isActuallyConnected ? <AssigneeInterfaceWrapper /> : <NotFoundPage />;
              }

              // Otherwise show dashboard or no wallet
              return isActuallyConnected ? <Dashboard /> : <NoWallet />;
            })()}
          </RouteLoaderWrapper>
        </BaseLayout>
      ),
    },
    {
      path: '/documents',
      element: (
        <BaseLayout isConnected={isActuallyConnected}>
          <RouteLoaderWrapper isLoading={isLoading}>
            {isActuallyConnected ? <DocumentsLayout /> : <NotFoundPage />}
          </RouteLoaderWrapper>
        </BaseLayout>
      ),
    },
    {
      path: '/signature-documents',
      element: (
        <BaseLayout isConnected={isActuallyConnected}>
          <RouteLoaderWrapper isLoading={isLoading}>
            {isActuallyConnected ? <ConnectedWalletLayout /> : <NotFoundPage />}
          </RouteLoaderWrapper>
        </BaseLayout>
      ),
    },
    {
      path: '/minted',
      element: (
        <BaseLayout isConnected={isActuallyConnected}>
          <RouteLoaderWrapper isLoading={isLoading}>
            {isActuallyConnected ? <MintedLayout /> : <NotFoundPage />}
          </RouteLoaderWrapper>
        </BaseLayout>
      ),
    },
    {
      path: '/transactions',
      element: (
        <BaseLayout isConnected={isActuallyConnected}>
          <RouteLoaderWrapper isLoading={isLoading}>
            {isActuallyConnected ? <TransactionsTable /> : <NotFoundPage />}
          </RouteLoaderWrapper>
        </BaseLayout>
      ),
    },
    {
      path: '/payment-history',
      element: (
        <BaseLayout isConnected={isActuallyConnected}>
          <RouteLoaderWrapper isLoading={isLoading}>
            {isActuallyConnected ? <PaymentHistoryTable /> : <NotFoundPage />}
          </RouteLoaderWrapper>
        </BaseLayout>
      ),
    },
    {
      path: '/viewDoc/:id',
      element: (
        <BaseLayout isConnected={isActuallyConnected}>
          <RouteLoaderWrapper isLoading={isLoading}>
            {isActuallyConnected ? (
              (() => {
                // Check which provider to use
                if (isCustom) {
                  // Custom provider - check for query params
                  const searchParams = new URLSearchParams(window.location.search);
                  const docId = searchParams.get('docId');
                  const token = searchParams.get('token');

                  if (docId && token) {
                    return <AssigneeInterfaceWrapper />;
                  }

                  // No query params but custom enabled - might be an error
                  console.warn('Custom signature enabled but no docId/token in URL');
                  return <NotFoundPage />;
                }

                // DocuSeal provider (default)
                return <DigitalSignatureEmbeddingDialog />;
              })()
            ) : (
              <NotFoundPage />
            )}
          </RouteLoaderWrapper>
        </BaseLayout>
      ),
    },
    { path: '*', element: <NotFoundPage /> },
  ];

  return useRoutes(routes);
}
